import {
  cloneDeep,
  filter,
  find,
  forEach,
  groupBy,
  head,
  includes,
  last,
  map,
  nth,
  orderBy,
  pick,
  reduce,
  reject,
  size,
  some,
  split,
  sumBy,
  take,
} from "lodash";
import { getFacetName } from "@linkfluence/i18n";
import { getContentClassificationColor } from "@linkfluence/models/content-classification";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { evolution, evolutionPercentagePoint, percent } from "@tootools/number";
import { SearchFragmentClassification } from "@radarly/models/search";

function rejectSeries(
  items: SearchFragmentClassification["data"]["aggs"]["contentClassification"]["values"],
  type: "head" | "last"
) {
  if (type === "head") {
    return reject(items, ({ key }) => split(key, "/").length > 1);
  } else {
    const allKeys = map(items, "key");
    const lastAllKeys = reduce(
      allKeys,
      (acc, key) => {
        if (
          !some(allKeys, (k) => {
            return k.indexOf(key + "/") !== -1;
          })
        ) {
          acc.push(key);
        }
        return acc;
      },
      []
    );
    return filter(items, ({ key }) => includes(lastAllKeys, key));
  }
}

export function computeSeries(
  items: SearchFragmentClassification["data"]["aggs"]["contentClassification"]["values"],
  metric: Metric,
  type: "head" | "last",
  locale: Locale,
  filterList: boolean = false,
  useParentColor?: boolean
) {
  return map(
    filterList ? rejectSeries(items, type) : items,
    ({ key, counts }) => {
      return {
        id: key,
        label: getFacetName(locale, Facet.ContentClassification, key),
        color: useParentColor
          ? getContentClassificationColor(head(split(key, "/")))
          : getContentClassificationColor(key),
        count: counts[metric],
      };
    }
  );
}

export function computeSeriesWithEvolutions(
  items: SearchFragmentClassification["data"]["aggs"]["contentClassification"]["values"],
  previousItems: SearchFragmentClassification["data"]["aggs"]["contentClassification"]["values"],
  metric: Metric,
  type: "head" | "last",
  locale: Locale,
  useParentColor?: boolean,
  mainUnit?: "percentage" | "total" | "average",
  currentTotalCount?: number,
  previousTotalCount?: number
) {
  const computedSeries = computeSeries(
    items,
    metric,
    type,
    locale,
    false,
    useParentColor
  );
  return map(computedSeries, (serie) => {
    const previousItem = find(previousItems, { key: serie.id });
    if (!previousItem) {
      return { ...serie, evolution: null };
    }
    const previousCount = previousItem.counts[metric];

    if (mainUnit === "percentage") {
      const before = percent(previousCount, previousTotalCount);
      const after = percent(serie.count, currentTotalCount);

      return {
        ...serie,
        evolution: evolutionPercentagePoint(before, after),
      };
    } else {
      return {
        ...serie,
        evolution: evolution(previousCount, serie.count),
      };
    }
  });
}

type TreeRoot = { id: string; children: TreeNode[] };
type TreeNode = {
  id: string;
  label: string;
  color: string;
  value: number;
  children?: TreeNode[];
  uid: string;
};

const findNode = (
  node: TreeRoot | TreeNode,
  id: string
): TreeRoot | TreeNode => {
  if (node.id === id) {
    return node;
  } else {
    for (let i = 0; i < size(node.children); i++) {
      const item = findNode(node.children[i], id);
      if (item) {
        return item;
      }
    }
  }
  return null;
};

const arrayToTree = (
  data: Array<{
    id: string;
    parentId: string;
    depth: number;
    label: string;
    color: string;
    value: number;
    uid: string;
  }>,
  nodesLimit?: number
) => {
  const root: TreeRoot = {
    id: "root",
    children: [],
  };
  forEach(data, (item) => {
    const parent = findNode(root, item.parentId);
    if (!!parent) {
      if (!nodesLimit || size(parent.children) < nodesLimit) {
        const treeItem = pick(cloneDeep(item), [
          "id",
          "label",
          "color",
          "value",
          "uid",
        ]);
        if (parent.children) {
          parent.children.push(treeItem);
        } else {
          parent.children = [treeItem];
        }
      }
    }
  });
  return root;
};

function takeByDepth(series, nodesLimitByDepth) {
  const result = [];
  const levels = groupBy(series, "depth");

  forEach(levels, (topics, levelIndex) => {
    if (levelIndex === "1") {
      result.push(...take(topics, nodesLimitByDepth));
    } else {
      const selectedParents = filter(result, {
        depth: Number(levelIndex) - 1,
      });
      const filtered = filter(topics, (topic) =>
        find(selectedParents, { id: topic.parentId })
      );
      result.push(...take(filtered, nodesLimitByDepth));
    }
  });
  return result;
}
const reduceClassificationTree = (tree, total) => {
  const topCategories = take(
    map(tree, "value")
      .sort(function (a, b) {
        return a - b;
      })
      .reverse(),
    10
  );
  const lastTopTenCategory = last(topCategories);
  return reject(orderBy(tree, "value", "desc"), (category) => {
    if (category.value < lastTopTenCategory || category.value < total * 0.005) {
      return true;
    } else {
      if (category.children) {
        category.children = orderBy(
          reduceClassificationTree(category.children, total),
          "value",
          "desc"
        );
      }
      return false;
    }
  });
};

const removeSmallCategories = (root) => {
  const Level1Total = sumBy(root.children, "value");
  return {
    ...root,
    children: reduceClassificationTree(root.children, Level1Total),
  };
};

export function computeSeriesToTree(
  items: SearchFragmentClassification["data"]["aggs"]["contentClassification"]["values"],
  metric: Metric,
  locale: Locale,
  nodesLimitByChildren?: number,
  nodesLimitByDepth?: number
) {
  const formated = orderBy(
    map(items, ({ key, counts }) => {
      const path = split(key, "/");
      const itemId = nth(path, path.length - 1);
      const parentId = nth(path, path.length - 2);
      return {
        id: itemId,
        parentId: itemId !== parentId ? parentId : "root",
        uid: key,
        depth: size(path),
        label: getFacetName(locale, Facet.ContentClassification, key),
        color: getContentClassificationColor(head(split(key, "/"))),
        value: counts[metric],
      };
    }),
    ["value", "depth"],
    ["desc", "asc"]
  );
  if (nodesLimitByDepth) {
    return removeSmallCategories(
      arrayToTree(takeByDepth(formated, nodesLimitByDepth))
    );
  } else {
    return removeSmallCategories(arrayToTree(formated, nodesLimitByChildren));
  }
}
